import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, IconButton, CircularProgress,
    Alert, TextField, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, FormControl, InputLabel, Select, MenuItem, Tooltip
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { API_BASE } from '../../apiBase';

export default function UserApiKeysDialog({ open, onClose, user }) {
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [userApiKeys, setUserApiKeys] = useState({}); // {provider: {last_validated_at, is_valid}}
    const [addKeyDialog, setAddKeyDialog] = useState(false);
    const [newKeyProvider, setNewKeyProvider] = useState('');
    const [newKeyValue, setNewKeyValue] = useState('');
    const [savingKey, setSavingKey] = useState(false);
    const [validatingKey, setValidatingKey] = useState(null);

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
    };

    const availableProviders = ['openai', 'anthropic', 'gemini'];

    useEffect(() => {
        if (open && user) {
            fetchUserApiKeys();
        }
    }, [open, user]);

    const fetchUserApiKeys = async () => {
        setLoading(true);
        setMessage('');
        setUserApiKeys({});
        setAddKeyDialog(false);
        setNewKeyProvider('');
        setNewKeyValue('');

        try {
            const res = await fetch(`${API_BASE}/admin/users/${user.id}/api-keys`, { headers });
            if (res.ok) {
                const data = await res.json();
                setUserApiKeys(data.configured_keys || {});
            }
        } catch (err) {
            console.error('Error fetching user API keys:', err);
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveApiKey = async () => {
        if (!newKeyProvider || !newKeyValue) {
            setMessage('Please select a provider and enter an API key');
            return;
        }

        setSavingKey(true);
        setMessage('');

        try {
            const res = await fetch(`${API_BASE}/admin/users/${user.id}/api-keys`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    provider: newKeyProvider,
                    api_key: newKeyValue
                })
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to save API key');
            }

            setMessage(`✅ API key for ${newKeyProvider} saved successfully`);
            setAddKeyDialog(false);
            setNewKeyProvider('');
            setNewKeyValue('');

            // Refresh user's keys
            await fetchUserApiKeys();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setSavingKey(false);
        }
    };

    const handleDeleteApiKey = async (provider) => {
        if (!window.confirm(`Delete ${provider} API key for user ${user.username}?`)) return;

        setMessage('');
        try {
            const res = await fetch(`${API_BASE}/admin/users/${user.id}/api-keys/${provider}`, {
                method: 'DELETE',
                headers
            });

            if (!res.ok) throw new Error('Failed to delete API key');

            setMessage(`✅ API key for ${provider} deleted`);

            // Refresh user's keys
            await fetchUserApiKeys();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    const handleValidateApiKey = async (provider) => {
        setValidatingKey(provider);
        try {
            const res = await fetch(`${API_BASE}/admin/users/${user.id}/api-keys/${provider}/validate`, {
                method: 'POST',
                headers
            });

            const result = await res.json();
            if (result.valid) {
                setMessage(`✅ ${provider} API key is valid`);
            } else {
                setMessage(`⚠️ ${provider} API key is invalid: ${result.error || 'Unknown error'}`);
            }

            // Refresh keys to get updated validation status
            await fetchUserApiKeys();
        } catch (err) {
            setMessage(`❌ Failed to validate: ${err.message}`);
        } finally {
            setValidatingKey(null);
        }
    };

    if (!user) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                    <DialogTitle>
                        Manage API Keys: {user.username}
                    </DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <Alert severity="info" icon={<HelpOutlineIcon />}>
                                Personal API keys override organization keys. Users in 'basic' mode typically use organization keys.
                            </Alert>

                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">Configured Personal API Keys</Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setAddKeyDialog(true)}
                                    startIcon={<VpnKeyIcon />}
                                >
                                    Add Key
                                </Button>
                            </Box>

                            {Object.keys(userApiKeys).length === 0 ? (
                                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No personal API keys configured
                                    </Typography>
                                </Paper>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Provider</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Last Validated</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(userApiKeys).map(([provider, info]) => (
                                                <TableRow key={provider}>
                                                    <TableCell>
                                                        <Chip label={provider} size="small" />
                                                    </TableCell>
                                                    <TableCell>
                                                        {info.is_valid === true && (
                                                            <Chip
                                                                icon={<CheckCircleIcon />}
                                                                label="Valid"
                                                                size="small"
                                                                color="success"
                                                            />
                                                        )}
                                                        {info.is_valid === false && (
                                                            <Chip
                                                                icon={<ErrorIcon />}
                                                                label="Invalid"
                                                                size="small"
                                                                color="error"
                                                            />
                                                        )}
                                                        {info.is_valid === null && (
                                                            <Chip
                                                                label="Not tested"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {info.last_validated_at ? (
                                                            <Typography variant="caption">
                                                                {new Date(info.last_validated_at).toLocaleString()}
                                                            </Typography>
                                                        ) : '—'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Validate">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleValidateApiKey(provider)}
                                                                disabled={validatingKey === provider}
                                                            >
                                                                {validatingKey === provider ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <CheckCircleIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteApiKey(provider)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {/* Add Key Sub-Dialog */}
                            {addKeyDialog && (
                                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                    <Typography variant="subtitle1" mb={2}>Add New API Key</Typography>
                                    <Box display="grid" gap={2}>
                                        <FormControl fullWidth>
                                            <InputLabel>Provider</InputLabel>
                                            <Select
                                                value={newKeyProvider}
                                                onChange={(e) => setNewKeyProvider(e.target.value)}
                                                label="Provider"
                                            >
                                                {availableProviders.map(p => (
                                                    <MenuItem key={p} value={p}>{p}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="API Key"
                                            type="password"
                                            value={newKeyValue}
                                            onChange={(e) => setNewKeyValue(e.target.value)}
                                            fullWidth
                                        />
                                        <Box display="flex" gap={1} justifyContent="flex-end">
                                            <Button onClick={() => setAddKeyDialog(false)}>
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={handleSaveApiKey}
                                                disabled={savingKey || !newKeyProvider || !newKeyValue}
                                            >
                                                {savingKey ? 'Saving...' : 'Save'}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
